import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Badge, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import * as postapi from "../../api/postapi";

const STEP = {
  SELECT_COST_CENTER: 1,
  PREVIEW: 2,
  RESULT: 3,
};

const ShipOrderWithTcsModal = ({ show, onHide, order, toggle }) => {
  const [step, setStep] = useState(STEP.SELECT_COST_CENTER);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Step 1
  const [costCenters, setCostCenters] = useState([]);
  const [selectedCostCenter, setSelectedCostCenter] = useState("");

  // Step 2
  const [preview, setPreview] = useState(null);
  const [cities, setCities] = useState([]);
  const [cityName, setCityName] = useState("");
  const [cityCode, setCityCode] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [codAmount, setCodAmount] = useState("");
  const [pieces, setPieces] = useState("");
  const [weightInKg, setWeightInKg] = useState("0.5");
  const [serviceCode, setServiceCode] = useState("");
  const [orderDetail, setOrderDetail] = useState("");
  const [fragile, setFragile] = useState(false);
  const [transactionNotes, setTransactionNotes] = useState("");
  const [sendSms, setSendSms] = useState(true);
  const [isEditingDetails, setIsEditingDetails] = useState(false);

  // Step 3
  const [result, setResult] = useState(null);
  const [labelLoading, setLabelLoading] = useState(false);

  const resetState = () => {
    setStep(STEP.SELECT_COST_CENTER);
    setIsLoading(false);
    setError("");
    setCostCenters([]);
    setSelectedCostCenter("");
    setPreview(null);
    setCities([]);
    setCityName("");
    setCityCode("");
    setOrderNumber("");
    setCustomerName("");
    setCustomerPhone("");
    setDeliveryAddress("");
    setCodAmount("");
    setPieces("");
    setWeightInKg("0.5");
    setServiceCode("");
    setOrderDetail("");
    setFragile(false);
    setTransactionNotes("");
    setSendSms(true);
    setIsEditingDetails(false);
    setResult(null);
    setLabelLoading(false);
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  useEffect(() => {
    if (show && order) {
      setIsLoading(true);
      setError("");
      postapi
        .getOrderTcsCostCenters()
        .then((res) => {
          setCostCenters(res.data?.data || []);
          setIsLoading(false);
        })
        .catch((err) => {
          setError(err?.response?.data?.error?.message || "Failed to load TCS cost centers.");
          setIsLoading(false);
        });
    }
  }, [show, order]);

  const handleCityChange = (name) => {
    setCityName(name);
    const match = cities.find((c) => (c?.cityname || "") === name);
    setCityCode(match?.citycode || "");
  };

  const handleNext = async () => {
    if (!selectedCostCenter) {
      setError("Please select a cost center.");
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      const [previewRes, citiesRes] = await Promise.allSettled([postapi.getOrderTcsPreview(order.id, selectedCostCenter), postapi.getOrderTcsCities()]);

      if (previewRes.status === "rejected") {
        throw new Error(previewRes.reason?.response?.data?.error?.message || "Failed to load order preview.");
      }

      const data = previewRes.value.data?.data;
      const cityList = citiesRes.status === "fulfilled" ? citiesRes.value.data?.data || [] : [];

      setCities(cityList);
      setPreview(data);
      setOrderNumber(data.order_number || "");
      setCustomerName(data.customerName || "");
      setCustomerPhone(data.customerPhone || "");
      setDeliveryAddress(data.deliveryAddress || "");
      setCodAmount(String(data.codAmount ?? ""));
      setPieces(String(data.pieces ?? ""));
      setWeightInKg(String(data.weightInKg ?? "0.5"));
      setServiceCode(data.serviceCode || "O");
      setOrderDetail(data.orderDetail || "");

      const resolution = data?.cityResolution;
      if (resolution?.ok && resolution.cityName) {
        setCityName(resolution.cityName);
        setCityCode(resolution.cityCode || "");
      } else if (resolution?.candidates?.length > 0) {
        const candidate = resolution.candidates[0].city || "";
        setCityName(candidate);
        const match = cityList.find((c) => (c?.cityname || "") === candidate);
        setCityCode(match?.citycode || "");
      } else {
        setCityName("");
        setCityCode("");
      }

      setStep(STEP.PREVIEW);
    } catch (err) {
      setError(err?.message || "Failed to load order preview.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!cityName) {
      setError("Please select a delivery city.");
      return;
    }
    if (!customerPhone.trim()) {
      setError("Customer phone is required.");
      return;
    }
    if (codAmount === "" || isNaN(Number(codAmount)) || Number(codAmount) < 0) {
      setError("Please enter a valid COD amount.");
      return;
    }
    if (Number(codAmount) > 199999) {
      setError("COD amount cannot exceed Rs. 199,999 (TCS limit).");
      return;
    }
    if (!serviceCode.trim()) {
      setError("Service code is required.");
      return;
    }
    if (!weightInKg || isNaN(Number(weightInKg)) || Number(weightInKg) < 0.5) {
      setError("Weight must be at least 0.5 kg.");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      const payload = {
        id: order.id,
        status: "Picked",
        delivery_partner_label: "TCS",
        delivery_partner_id: order.deliveryPartnerId,
        cityName,
        cityCode,
        costCenterCode: selectedCostCenter,
        serviceCode: serviceCode.trim(),
        weightInKg: Number(weightInKg),
        pieces: pieces ? Number(pieces) : undefined,
        fragile,
        transactionNotes: transactionNotes.trim(),
        send_sms: sendSms,
        orderNumber: orderNumber.trim(),
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        deliveryAddress: deliveryAddress.trim(),
        invoicePayment: Number(codAmount),
        orderDetail: orderDetail.trim(),
      };
      const res = await postapi.changeOrderStatus(payload);
      setResult(res.data?.data);
      setStep(STEP.RESULT);
      toggle();
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Failed to create booking with TCS.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadLabel = async () => {
    try {
      setLabelLoading(true);
      const res = await postapi.getOrderTcsLabel(result.trackingNumber);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tcs-label-${result.trackingNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download TCS label.");
    } finally {
      setLabelLoading(false);
    }
  };

  const resolution = preview?.cityResolution;

  const renderCityDropdown = () => {
    if (cities.length > 0) {
      return (
        <Form.Select value={cityName} onChange={(e) => handleCityChange(e.target.value)}>
          <option value="">— Select city —</option>
          {cities.map((c) => {
            const name = c?.cityname || "";
            return (
              <option key={c?.citycode || name} value={name}>
                {name}
              </option>
            );
          })}
        </Form.Select>
      );
    }
    if (resolution?.candidates?.length > 0) {
      return (
        <Form.Select value={cityName} onChange={(e) => handleCityChange(e.target.value)}>
          <option value="">— Select city —</option>
          {resolution.candidates.map((c) => (
            <option key={c.city} value={c.city}>
              {c.city} (score: {c.score})
            </option>
          ))}
        </Form.Select>
      );
    }
    return <Form.Control type="text" value={cityName} onChange={(e) => handleCityChange(e.target.value)} placeholder="Enter TCS city name" />;
  };

  const renderStep1 = () => (
    <>
      <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
        Select the TCS cost center (pickup location) for this booking.
      </p>
      {isLoading ? (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        <>
          <Form.Label className="fw-semibold mb-2">
            Cost Center <span className="text-danger">*</span>
          </Form.Label>
          <div className="d-flex flex-column gap-2">
            {costCenters.map((cc) => {
              const isSelected = selectedCostCenter === cc.costcentercode;
              return (
                <div
                  key={cc.costcentercode}
                  onClick={() => {
                    setSelectedCostCenter(cc.costcentercode);
                    setError("");
                  }}
                  style={{
                    cursor: "pointer",
                    border: `2px solid ${isSelected ? "#0d6efd" : "#dee2e6"}`,
                    borderRadius: "8px",
                    padding: "10px 14px",
                    backgroundColor: isSelected ? "#f0f6ff" : "#fff",
                    fontSize: "13px",
                    transition: "border-color 0.15s, background-color 0.15s",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-semibold mb-1" style={{ fontSize: "14px" }}>
                        {cc.costcentername}
                        {cc.costcentercity && (
                          <span className="ms-2 text-muted fw-normal" style={{ fontSize: "12px" }}>
                            {cc.costcentercity}
                          </span>
                        )}
                      </div>
                      <div className="text-muted">{cc.pickupaddress}</div>
                      {cc.phoneno && <div className="text-muted mt-1">📞 {cc.phoneno}</div>}
                      <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                        Code: {cc.costcentercode}
                      </div>
                    </div>
                    <div className="ms-3 mt-1">
                      <div
                        style={{
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          border: `2px solid ${isSelected ? "#0d6efd" : "#adb5bd"}`,
                          backgroundColor: isSelected ? "#0d6efd" : "transparent",
                          flexShrink: 0,
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {!!error && !selectedCostCenter && (
            <div className="text-danger mt-1" style={{ fontSize: "13px" }}>
              {error}
            </div>
          )}
        </>
      )}
    </>
  );

  const renderStep2 = () => (
    <>
      {preview && (
        <>
          <div className="mb-3 p-3 border rounded" style={{ backgroundColor: "#f8f9fa", fontSize: "13px" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0 fw-semibold">Order Details</h6>
              {!isEditingDetails ? (
                <Button size="sm" variant="link" className="p-0 text-secondary" style={{ fontSize: "12px" }} onClick={() => setIsEditingDetails(true)}>
                  <FontAwesomeIcon icon={faPencil} className="me-1" />
                  Edit
                </Button>
              ) : (
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 text-danger"
                    style={{ fontSize: "12px" }}
                    onClick={() => {
                      setOrderNumber(preview.order_number || "");
                      setCustomerName(preview.customerName || "");
                      setCustomerPhone(preview.customerPhone || "");
                      setDeliveryAddress(preview.deliveryAddress || "");
                      setCodAmount(String(preview.codAmount ?? ""));
                      setPieces(String(preview.pieces ?? ""));
                      setOrderDetail(preview.orderDetail || "");
                      setIsEditingDetails(false);
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} className="me-1" />
                    Cancel
                  </Button>
                  <Button size="sm" variant="link" className="p-0 text-success" style={{ fontSize: "12px" }} onClick={() => setIsEditingDetails(false)}>
                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                    Done
                  </Button>
                </div>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Order #
              </span>
              {isEditingDetails ? <Form.Control size="sm" type="text" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Order number" /> : <span className="fw-medium">{orderNumber}</span>}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Customer
              </span>
              {isEditingDetails ? <Form.Control size="sm" type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name" /> : <span>{customerName}</span>}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Phone <span className="text-danger">*</span>
              </span>
              {isEditingDetails ? <Form.Control size="sm" type="text" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="e.g. 03001234567" /> : <span>{customerPhone}</span>}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Address
              </span>
              {isEditingDetails ? (
                <Form.Control size="sm" as="textarea" rows={2} value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} placeholder="Delivery address" />
              ) : (
                <span className="text-end" style={{ maxWidth: "60%" }}>
                  {deliveryAddress}
                </span>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                COD (Rs.) <span className="text-danger">*</span>
              </span>
              {isEditingDetails ? (
                <InputGroup size="sm">
                  <InputGroup.Text>Rs.</InputGroup.Text>
                  <Form.Control type="number" min="0" max="199999" value={codAmount} onChange={(e) => setCodAmount(e.target.value)} placeholder="e.g. 5500" />
                </InputGroup>
              ) : (
                <span className="fw-medium">Rs. {Number(codAmount).toLocaleString()}</span>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-center border-bottom py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Pieces
              </span>
              {isEditingDetails ? <Form.Control size="sm" type="number" min="1" value={pieces} onChange={(e) => setPieces(e.target.value)} placeholder="No. of pieces" /> : <span>{pieces}</span>}
            </div>
            <div className="d-flex justify-content-between align-items-center py-1 gap-2">
              <span className="text-muted" style={{ minWidth: "110px" }}>
                Item Detail
              </span>
              {isEditingDetails ? (
                <Form.Control size="sm" as="textarea" rows={2} value={orderDetail} onChange={(e) => setOrderDetail(e.target.value)} placeholder="Item description" />
              ) : (
                <span className="text-end" style={{ maxWidth: "60%" }}>
                  {orderDetail}
                </span>
              )}
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>
              Delivery City <span className="text-danger">*</span>
              {resolution?.ok && (
                <Badge bg="success" className="ms-2" style={{ fontSize: "11px" }}>
                  Auto-detected
                </Badge>
              )}
              {!resolution?.ok && (
                <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: "11px" }}>
                  Please confirm
                </Badge>
              )}
            </Form.Label>
            {renderCityDropdown()}
            {resolution && !resolution.ok && <Form.Text className="text-warning">{resolution.reason || "Could not auto-detect city from address."}</Form.Text>}
          </Form.Group>

          <div className="d-flex gap-3 mb-3">
            <Form.Group className="flex-fill">
              <Form.Label>
                Weight (kg) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control type="number" min="0.5" step="0.1" value={weightInKg} onChange={(e) => setWeightInKg(e.target.value)} />
            </Form.Group>
            <Form.Group className="flex-fill">
              <Form.Label>
                Service <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select value={serviceCode} onChange={(e) => setServiceCode(e.target.value)}>
                <option value="O">O - Express</option>
                <option value="OD">OD - Economy Express</option>
                <option value="OLE">OLE - Overland Ecom</option>
              </Form.Select>
            </Form.Group>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Remarks</Form.Label>
            <Form.Control as="textarea" rows={2} maxLength={500} value={transactionNotes} onChange={(e) => setTransactionNotes(e.target.value)} placeholder="Optional remarks for TCS" />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Check type="switch" id="tcs-fragile-toggle" label="Fragile shipment" checked={fragile} onChange={(e) => setFragile(e.target.checked)} />
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Check type="switch" id="tcs-send-sms-toggle" label="Send SMS to customer with tracking number" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} />
          </Form.Group>
        </>
      )}
    </>
  );

  const renderStep3 = () => (
    <div className="text-center py-2">
      <div className="mb-3">
        <span style={{ fontSize: "48px" }}>✅</span>
      </div>
      <h5 className="fw-semibold text-success mb-1">Booking Created!</h5>
      <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
        The booking has been created on TCS and the order status updated to <strong>Picked</strong>.
      </p>
      <div className="p-3 border rounded mb-3" style={{ backgroundColor: "#f8f9fa", fontSize: "13px" }}>
        <div className="d-flex justify-content-between border-bottom py-1">
          <span className="text-muted">Consignment #</span>
          <span className="fw-bold text-primary">{result?.trackingNumber}</span>
        </div>
        <div className="d-flex justify-content-between py-1">
          <span className="text-muted">TCS Response</span>
          <span>{result?.orderStatus}</span>
        </div>
      </div>
      <Button variant="outline-primary" size="sm" onClick={handleDownloadLabel} disabled={labelLoading}>
        {labelLoading ? <Spinner animation="border" size="sm" /> : "Download Label (PDF)"}
      </Button>
    </div>
  );

  const getTitle = () => {
    if (step === STEP.SELECT_COST_CENTER) return "Ship With TCS — Select Cost Center";
    if (step === STEP.PREVIEW) return "Ship With TCS — Preview & Confirm";
    return "Ship With TCS — Done";
  };

  return (
    <Modal show={show} onHide={handleClose} size="md" centered backdrop="static" aria-labelledby="tcs-modal-title">
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="tcs-modal-title" style={{ fontSize: "16px" }}>
          {getTitle()}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 pb-2">
        {error && (
          <div className="text-danger text-center mb-3 rounded-1 w-100" style={{ fontSize: "13px" }}>
            * {error}
          </div>
        )}
        {step === STEP.SELECT_COST_CENTER && renderStep1()}
        {step === STEP.PREVIEW && renderStep2()}
        {step === STEP.RESULT && renderStep3()}
      </Modal.Body>
      <Modal.Footer className="border-0 px-4 pb-4 pt-2">
        {step === STEP.SELECT_COST_CENTER && (
          <div className="d-flex gap-3 w-100">
            <span className="w-100 d-flex justify-content-center align-items-center text-secondary p-2" style={{ cursor: "pointer", border: "2px solid rgba(151, 156, 162, 1)", borderRadius: "6px" }} onClick={handleClose}>
              Cancel
            </span>
            <Button className="w-100" onClick={handleNext} disabled={isLoading || !selectedCostCenter}>
              {isLoading ? <Spinner animation="border" size="sm" /> : "Next"}
            </Button>
          </div>
        )}
        {step === STEP.PREVIEW && (
          <div className="d-flex gap-3 w-100">
            <span
              className="w-100 d-flex justify-content-center align-items-center text-secondary p-2"
              style={{ cursor: "pointer", border: "2px solid rgba(151, 156, 162, 1)", borderRadius: "6px" }}
              onClick={() => {
                setStep(STEP.SELECT_COST_CENTER);
                setError("");
              }}
            >
              Back
            </span>
            <Button className="w-100" onClick={handleConfirm} disabled={isLoading || !cityName.trim()}>
              {isLoading ? <Spinner animation="border" size="sm" /> : "Confirm & Pick"}
            </Button>
          </div>
        )}
        {step === STEP.RESULT && (
          <Button className="w-100" onClick={handleClose}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ShipOrderWithTcsModal;
